import {
  Button,
  Flex,
  Input,
  Modal,
  Table,
  TableProps,
  Descriptions,
  Typography,
  Tag,
  Select,
  Popover,
  Badge,
  Empty,
  Tooltip,
  Collapse,
} from "antd";
import { RegisteredUser } from "../../models/registration";
import { Dispatch, SetStateAction, useState } from "react";
import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import {
  IconSearch,
  IconFilter,
  IconFileText,
  IconEye,
} from "@tabler/icons-react";
import FormResponseFilePreview from "../../components/registration/FormResponseFilePreview";
import {
  FILE_FIELD_KEYS,
  formatFormResponseScalar,
  getDisplayableFormResponseEntries,
  getFormResponseLabel,
  isMediaFileArray,
  normalizeMediaFiles,
} from "../../utils/registrationFormResponse";
import { getRegistrationStatusDisplay } from "../../utils/registrationStatus";

// const PREVIEW_KEYS: (keyof RegistrationFormResponse)[] = [
//   "gender",
//   "tshirt_size",
//   "blood_group",
//   "nationality",
//   "dob",
//   "city",
// ];

const RegistrationTable = ({
  loading,
  data,
  pageSize,
  setPage,
  setPageSize,
  setSearch,
  search,
  filters,
  setFilters,
  ticketOptions,
}: {
  loading: boolean;
  data: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    data: RegisteredUser[];
  } | null;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
  setPageSize: Dispatch<SetStateAction<number>>;
  setSearch: Dispatch<SetStateAction<string | undefined>>;
  search: string | undefined;
  filters: {
    quantity?: number;
    ticket_id?: string;
    payment_method?: string;
    email?: string;
    subtotal?: string | number;
    gender?: string;
    tshirt_size?: string;
    blood_group?: string;
    nationality?: string;
    city?: string;
    country?: string;
  };
  setFilters: Dispatch<
    SetStateAction<{
      quantity?: number;
      ticket_id?: string;
      payment_method?: string;
      email?: string;
      subtotal?: string | number;
      gender?: string;
      tshirt_size?: string;
      blood_group?: string;
      nationality?: string;
      city?: string;
      country?: string;
    }>
  >;
  ticketOptions: { label: string; value: string }[];
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RegisteredUser | null>(
    null,
  );

  // Pick up to 2 non-null priority fields for inline preview
  // const renderFormPreview = (record: RegisteredUser) => {
  //   const fr = record.registration_form_response;
  //   if (!fr) return <Typography.Text type="secondary">—</Typography.Text>;

  //   const previews = PREVIEW_KEYS.filter(
  //     (k) => fr[k] !== null && fr[k] !== undefined && fr[k] !== "",
  //   ).slice(0, 2);

  //   if (previews.length === 0)
  //     return <Typography.Text type="secondary">—</Typography.Text>;

  //   return (
  //     <Flex vertical gap={2}>
  //       {previews.map((key) => (
  //         <Tooltip key={key} title={`${getFormResponseLabel(key)}: ${fr[key]}`}>
  //           <Typography.Text
  //             ellipsis
  //             style={{ fontSize: 12, maxWidth: 170, display: "block" }}
  //           >
  //             <span style={{ color: "#8c8c8c" }}>
  //               {getFormResponseLabel(key)}:
  //             </span>{" "}
  //             {String(fr[key])}
  //           </Typography.Text>
  //         </Tooltip>
  //       ))}
  //     </Flex>
  //   );
  // };

  const columns: TableProps<RegisteredUser>["columns"] = [
    {
      title: "Full Name",
      dataIndex: "first_name",
      key: "first_name",
      render: (_, record) =>
        (record.first_name || "") + " " + (record.last_name || ""),
    },
    {
      title: "Phone No",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (value) => value || "N/A",
    },
    {
      title: "Ticket Type",
      dataIndex: "ticket_name",
      key: "ticket_name",
      render: (value) => value || "N/A",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => <span>{record?.quantity ?? "—"}</span>,
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (_, record) => (
        <span>
          {record?.order?.payment_method?.toUpperCase() === "COD"
            ? "Invite Only"
            : record?.order?.payment_method}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "ticket_price",
      key: "ticket_price",
      render: (value, record) => {
        const amount = parseFloat(value);
        if (amount === 0) {
          return record?.is_transferred ? "Transfer" : "Free";
        }
        if (record?.is_transferred) {
          return "Transfer";
        }
        return getCurrencyWithSymbol(record.currency, amount);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const { label, color } = getRegistrationStatusDisplay(record);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Discount",
      key: "organizer_discount_price",
      render: (_, record) => <Flex>{record?.organizer_discount_price}</Flex>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Flex gap={0} align="center">
          <Tooltip title="View">
            <Button
              type="link"
              size="small"
              icon={<IconEye size={14} />}
              onClick={() => {
                setSelectedRecord(record);
                setDetailsOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Form Responses">
            <Button
              type="link"
              size="small"
              icon={<IconFileText size={14} />}
              onClick={() => {
                setSelectedRecord(record);
                setResponsesOpen(true);
              }}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const setFilter = (key: string, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filterContent = (
    <div
      style={{
        width: 300,
        maxHeight: "60vh",
        overflowY: "auto",
        padding: "8px 0",
      }}
    >
      <Flex vertical gap={0}>
        <Collapse
          ghost
          defaultActiveKey={["registration", "form"]}
          items={[
            {
              key: "registration",
              label: (
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Registration
                </span>
              ),
              children: (
                <Flex vertical gap={10}>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">Ticket Type</span>
                    <Select
                      placeholder="Select Ticket"
                      allowClear
                      value={filters.ticket_id}
                      onChange={(v) => setFilter("ticket_id", v)}
                      options={ticketOptions}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement!}
                    />
                  </Flex>
                </Flex>
              ),
            },
            {
              key: "form",
              label: (
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Form Response
                </span>
              ),
              children: (
                <Flex vertical gap={10}>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">Gender</span>
                    <Select
                      placeholder="Select Gender"
                      allowClear
                      value={filters.gender}
                      onChange={(v) => setFilter("gender", v)}
                      options={[
                        { label: "Male", value: "male" },
                        { label: "Female", value: "female" },
                        { label: "Other", value: "other" },
                      ]}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement!}
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">T-Shirt Size</span>
                    <Select
                      placeholder="Select Size"
                      allowClear
                      value={filters.tshirt_size}
                      onChange={(v) => setFilter("tshirt_size", v)}
                      options={[
                        { label: "XS", value: "XS" },
                        { label: "S", value: "S" },
                        { label: "M", value: "M" },
                        { label: "L", value: "L" },
                        { label: "XL", value: "XL" },
                        { label: "XXL", value: "XXL" },
                      ]}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement!}
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">Blood Group</span>
                    <Select
                      placeholder="Select Blood Group"
                      allowClear
                      value={filters.blood_group}
                      onChange={(v) => setFilter("blood_group", v)}
                      options={[
                        { label: "A+", value: "A+" },
                        { label: "A-", value: "A-" },
                        { label: "B+", value: "B+" },
                        { label: "B-", value: "B-" },
                        { label: "AB+", value: "AB+" },
                        { label: "AB-", value: "AB-" },
                        { label: "O+", value: "O+" },
                        { label: "O-", value: "O-" },
                      ]}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement!}
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">Nationality</span>
                    <Input
                      placeholder="e.g. Indian"
                      allowClear
                      value={filters.nationality}
                      onChange={(e) =>
                        setFilter("nationality", e.target.value || undefined)
                      }
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">City</span>
                    <Input
                      placeholder="e.g. Ahmedabad"
                      allowClear
                      value={filters.city}
                      onChange={(e) =>
                        setFilter("city", e.target.value || undefined)
                      }
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <span className="text-xs text-gray-400">Country</span>
                    <Input
                      placeholder="e.g. India"
                      allowClear
                      value={filters.country}
                      onChange={(e) =>
                        setFilter("country", e.target.value || undefined)
                      }
                    />
                  </Flex>
                </Flex>
              ),
            },
          ]}
        />
        <div style={{ padding: "4px 16px 8px" }}>
          <Button
            type="link"
            danger
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            Reset All Filters
          </Button>
        </div>
      </Flex>
    </div>
  );

  const fr = selectedRecord?.registration_form_response;
  const formResponseEntries = fr ? getDisplayableFormResponseEntries(fr) : [];

  const renderFormResponseValue = (key: string, value: unknown) => {
    if (FILE_FIELD_KEYS.has(key) || isMediaFileArray(value)) {
      return <FormResponseFilePreview value={value} />;
    }

    if (typeof value === "boolean") {
      return value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>;
    }

    if (
      typeof value === "string" &&
      value.startsWith("http") &&
      normalizeMediaFiles(value).length > 0
    ) {
      return <FormResponseFilePreview value={value} />;
    }

    return formatFormResponseScalar(key, value as string | number | boolean);
  };

  return (
    <>
      <Table<RegisteredUser>
        scroll={{ x: "max-content" }}
        rowKey={"id"}
        columns={columns}
        loading={loading}
        dataSource={data?.data}
        bordered={true}
        title={() => (
          <Flex
            className="w-full"
            justify="space-between"
            align="center"
            gap={20}
            wrap="wrap"
          >
            <Flex gap={12} align="center">
              <Popover
                content={filterContent}
                title="Filters"
                trigger="click"
                placement="bottomLeft"
              >
                <Badge count={activeFilterCount} size="small" offset={[0, 0]}>
                  <Button icon={<IconFilter size={18} />}>Filters</Button>
                </Badge>
              </Popover>
            </Flex>
            <Input
              className="!w-auto"
              suffix={<IconSearch size={16} color="gray" />}
              placeholder="Search Name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Flex>
        )}
        pagination={{
          current: data?.current_page,
          pageSize: pageSize,
          total: data?.total,
          onChange: (page, size) => {
            setPage(page);
            setPageSize(size);
          },
        }}
      />

      {/* Registration Details Modal */}
      <Modal
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        footer={null}
        title="Registration Details"
        width={700}
      >
        {selectedRecord && (
          <div style={{ padding: "16px 0" }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Full Name">
                {selectedRecord.first_name || "N/A"}{" "}
                {selectedRecord.last_name || ""}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedRecord.email || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedRecord.phone || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Ticket Type">
                {selectedRecord.ticket_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Quantity">
                {selectedRecord.quantity ?? "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {selectedRecord.order?.payment_method?.toUpperCase() === "COD"
                  ? "Invite Only"
                  : selectedRecord.order?.payment_method || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {selectedRecord.subtotal
                  ? parseFloat(selectedRecord.subtotal) === 0
                    ? "Free"
                    : getCurrencyWithSymbol(
                        selectedRecord.currency,
                        parseFloat(selectedRecord.subtotal),
                      )
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getRegistrationStatusDisplay(selectedRecord).color}>
                  {getRegistrationStatusDisplay(selectedRecord).label}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Form Responses Modal */}
      <Modal
        open={responsesOpen}
        onCancel={() => setResponsesOpen(false)}
        footer={null}
        title={
          <Flex align="center" gap={8}>
            <IconFileText size={20} color="#1677ff" />
            <span>Form Responses</span>
            {selectedRecord && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 13, fontWeight: 400 }}
              >
                — {selectedRecord.first_name} {selectedRecord.last_name}
              </Typography.Text>
            )}
          </Flex>
        }
        width={860}
        destroyOnClose
      >
        {!fr ? (
          <Empty
            description="No form response submitted"
            style={{ padding: "40px 0" }}
          />
        ) : formResponseEntries.length === 0 ? (
          <Empty
            description="No form fields to display"
            style={{ padding: "40px 0" }}
          />
        ) : (
          <div style={{ padding: "12px 0" }}>
            <Descriptions bordered column={2} size="small">
              {formResponseEntries.map(({ key, value }) => (
                <Descriptions.Item
                  key={key}
                  label={getFormResponseLabel(key)}
                  span={
                    FILE_FIELD_KEYS.has(key) || isMediaFileArray(value) ? 2 : 1
                  }
                >
                  {renderFormResponseValue(key, value)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RegistrationTable;
